import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class GoogleTranslationService {
  private translateRateLimitedUntil: number = 0;

  constructor(private http: HttpClient, private snackBar: MatSnackBar) {}

  async translateText(text: string, target: string): Promise<string> {
    if (!text || target === 'en') return text;
    if (this.translateRateLimitedUntil && Date.now() < this.translateRateLimitedUntil) {
      return text;
    }

    const apiKey = environment.googleApiKey;
    if (!apiKey) {
      // Only show this once or logged elsewhere to avoid spamming user
      console.warn('Translate API key missing');
      return text;
    }

    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`;

    try {
      const resp: any = await this.http
        .post(url, { q: text, target: target, format: 'text' })
        .toPromise();
      const translations = (resp && resp.data && resp.data.translations) || (resp && resp.translations);
      const first = translations && translations[0];
      return (first && first.translatedText) || text;
    } catch (e) {
      this.handleError(e);
      return text;
    }
  }

  async translateBatch(texts: string[], target: string): Promise<string[]> {
    if (!texts || !texts.length) return [];
    
    // Always return a copy to prevent reference sharing
    if (target === 'en') return texts.slice();
    
    if (this.translateRateLimitedUntil && Date.now() < this.translateRateLimitedUntil) {
      return texts.slice();
    }

    const apiKey = environment.googleApiKey;
    if (!apiKey) {
      console.warn('Translate API key missing');
      return texts.slice();
    }

    // Filter out empty strings but keep track of indices to reconstruct array correctly
    const indices: number[] = [];
    const clean: string[] = [];
    
    texts.forEach((t, i) => {
      if (t && t.trim()) {
        indices.push(i);
        clean.push(t);
      }
    });

    // If nothing to translate, return copy of original
    if (!clean.length) {
      return texts.slice();
    }

    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`;

    try {
      const resp: any = await this.http
        .post(url, { q: clean, target: target, format: 'text' })
        .toPromise();
      
      const translations = (resp && resp.data && resp.data.translations) || [];
      
      // Reconstruct array preserving original structure and empty slots
      const result = texts.slice();
      
      translations.forEach((t: any, i: number) => {
        const originalIndex = indices[i];
        if (originalIndex !== undefined) {
          result[originalIndex] = (t && t.translatedText) || clean[i];
        }
      });
      
      return result;
    } catch (e) {
      this.handleError(e);
      // Return copy of original texts on error
      return texts.slice();
    }
  }

  private handleError(e: any) {
    try {
      const errorObj = e && e.error && e.error.error;
      const code = errorObj && errorObj.code;
      const errors = errorObj && errorObj.errors;
      const reason = errors && errors[0] && errors[0].reason;
      
      if (code === 403 && reason === 'userRateLimitExceeded') {
        this.translateRateLimitedUntil = Date.now() + 60000;
        this.snackBar.open('Translate rate limit reached. Retrying later.', 'OK', { duration: 3000 });
      } else {
        console.error('Translation error:', e);
        // Optional: show generic error
        // this.snackBar.open('Translation failed', 'OK', { duration: 2000 });
      }
    } catch (_) {}
  }
}
